import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { HardDrive, AlertTriangle, XCircle, Database } from 'lucide-react';
import Spinner from '../components/ui/Spinner';
import Badge from '../components/ui/Badge';
import { getStorageOverview, getStorageRanking } from '../services/storageService';
import { formatBytes } from '../utils/dateUtils';

const PLAN_BADGE: Record<string, 'default' | 'info' | 'success'> = { FREE: 'default', PRO: 'info', ENTERPRISE: 'success' };
const STATUS_CFG: Record<string, { variant: 'success' | 'warning' | 'error'; label: string }> = {
  normal: { variant: 'success', label: '정상' },
  warning: { variant: 'warning', label: '경고' },
  critical: { variant: 'error', label: '차단' },
};

export default function StoragePage() {
  const navigate = useNavigate();
  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ['admin', 'storage', 'overview'],
    queryFn: getStorageOverview,
  });
  const { data: ranking, isLoading: rankingLoading } = useQuery({
    queryKey: ['admin', 'storage', 'ranking'],
    queryFn: () => getStorageRanking(20),
  });

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">스토리지 모니터링</h1>

      {overviewLoading ? (
        <div className="flex justify-center py-12"><Spinner className="size-8" /></div>
      ) : overview ? (
        <>
          {/* 전체 현황 카드 */}
          <div className="grid grid-cols-4 gap-4">
            <OverviewCard icon={Database} label="전체 사용량"
              value={formatBytes(overview.totalUsed)} sub={`${overview.usageRate}%`} />
            <OverviewCard icon={HardDrive} label="전체 한도"
              value={formatBytes(overview.totalLimit)} sub={`${overview.organizationCount}개 조직`} />
            <OverviewCard icon={AlertTriangle} label="경고 조직" value={String(overview.warningCount)}
              sub="80% 이상" className={overview.warningCount > 0 ? 'bg-orange-50 border-orange-200' : ''} />
            <OverviewCard icon={XCircle} label="차단 조직" value={String(overview.criticalCount)}
              sub="100%" className={overview.criticalCount > 0 ? 'bg-red-50 border-red-200' : ''} />
          </div>

          {/* 로컬 디스크 */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-medium text-gray-700 mb-3">로컬 디스크</h3>
            {overview.disk ? (
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex-1 h-4 bg-gray-200 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${overview.disk.usageRate >= 90 ? 'bg-red-500' : 'bg-blue-500'}`}
                      style={{ width: `${Math.min(overview.disk.usageRate, 100)}%` }} />
                  </div>
                  <span className="text-sm text-gray-600 font-medium shrink-0">
                    {formatBytes(overview.disk.used)} / {formatBytes(overview.disk.total)} ({overview.disk.usageRate}%)
                  </span>
                </div>
                {overview.disk.usageRate >= 90 && (
                  <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm flex items-center gap-2 mt-2">
                    <AlertTriangle className="size-4 shrink-0" />
                    디스크 용량이 부족합니다. 서버 확장을 검토하세요.
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-400">디스크 정보를 가져올 수 없습니다.</p>
            )}
          </div>
        </>
      ) : null}

      {/* 조직별 랭킹 */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">조직별 스토리지 사용량</h3>
          <span className="text-sm text-gray-400">Top 20</span>
        </div>
        {rankingLoading ? (
          <div className="flex justify-center py-12"><Spinner className="size-6" /></div>
        ) : !ranking || ranking.items.length === 0 ? (
          <p className="text-center text-sm text-gray-400 py-8">데이터가 없습니다.</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="px-5 py-3 text-left text-sm font-semibold text-gray-500 uppercase">조직명</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase">플랜</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase w-72">사용량</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase">상태</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {ranking.items.map((org) => {
                const st = STATUS_CFG[org.status] ?? STATUS_CFG.normal;
                const barColor = org.status === 'critical' ? 'bg-red-500' : org.status === 'warning' ? 'bg-orange-500' : 'bg-blue-500';
                const rowBg = org.status === 'critical' ? 'bg-red-50' : org.status === 'warning' ? 'bg-orange-50' : '';
                return (
                  <tr key={org.id} className={`hover:bg-gray-50 ${rowBg}`}>
                    <td className="px-5 py-3">
                      <button onClick={() => navigate(`/organizations/${org.id}`)}
                        className="text-sm font-medium text-primary-600 hover:underline">{org.name}</button>
                      <div className="text-sm text-gray-400">{org.slug}</div>
                    </td>
                    <td className="px-4 py-3"><Badge variant={PLAN_BADGE[org.planType] ?? 'default'}>{org.planType}</Badge></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${barColor}`} style={{ width: `${Math.min(org.usageRate, 100)}%` }} />
                        </div>
                        <span className="text-sm text-gray-500 w-10 text-right">{org.usageRate}%</span>
                      </div>
                      <p className="text-sm text-gray-400">{formatBytes(org.storageUsedBytes)} / {formatBytes(org.storageLimitBytes)}</p>
                    </td>
                    <td className="px-4 py-3"><Badge variant={st.variant}>{st.label}</Badge></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function OverviewCard({ icon: Icon, label, value, sub, className = '' }: {
  icon: typeof HardDrive; label: string; value: string; sub?: string; className?: string;
}) {
  return (
    <div className={`bg-white rounded-xl border border-gray-200 p-5 ${className}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="size-4 text-gray-400" />
        <span className="text-sm text-gray-500">{label}</span>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-sm text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );
}
